// apps/web/src/pages/spread/BatchSetRecipePicker.tsx
//
// Phase 9 Plan 09-12 Task 2 — per-batch-set recipe picker (Combobox).
//
// COMPOSITION PER UI-SPEC §"Recipe picker (Combobox per batch set)" (lines 380-392):
//   <Popover open={open}>
//     <PopoverTrigger>{selectedRecipeName ?? "Pick a recipe…"}</PopoverTrigger>
//     <PopoverContent>
//       <Command>
//         <CommandInput placeholder="Search recipes…" />
//         <CommandList>
//           <CommandEmpty>No recipes match "{query}"</CommandEmpty>
//           <CommandGroup heading="RECENTLY USED">...recent recipes...</CommandGroup>
//           <CommandGroup heading="ALL ACCESSIBLE RECIPES">...all recipes...</CommandGroup>
//         </CommandList>
//       </Command>
//     </PopoverContent>
//   </Popover>
//
// API:
//   GET /api/recipes is the Phase 5 read endpoint. Phase 5 returned the basic
//   recipe list (id, owner_id, slug, visibility). There is NO ?accessible=true
//   filter today — the endpoint is already RLS-filtered to recipes the actor
//   can read. We render the bare /api/recipes response and treat every row as
//   accessible. If Phase 9+ adds typed recipe names + owners to the list, this
//   picker picks them up automatically.
//
// REPLACE-CONFIRM (UI-SPEC line 392):
//   When the user picks a recipe AND the form-state for this batch set already
//   has user-selected options (BatchSet.selections has entries), a destructive
//   Dialog asks "Replace recipe? Selecting {new} will clear your current
//   option selections in this {subdivision}." The clearing happens via
//   form.setValue on `batchSets.${index}.selections` to {} when confirmed.
//
// FORM-STATE PATH: `batchSets.${index}.recipeId`. The picker writes back via
// the parent's setValue (passed through FormField -> field.onChange).

import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTerm } from "@/lib/vocabulary/useTerm";

interface RecipeListItem {
  id: string;
  owner_id?: string;
  slug?: string;
  name?: string;
  visibility?: string;
}

interface RecipeListResponse {
  items: RecipeListItem[];
  count?: number;
}

export interface BatchSetRecipePickerProps {
  /** Index into the batchSets RHF field array. */
  index: number;
}

export function BatchSetRecipePicker({ index }: BatchSetRecipePickerProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  const subdivision = useTerm("subdivision");
  const [open, setOpen] = useState(false);
  const [pendingReplaceRecipe, setPendingReplaceRecipe] = useState<RecipeListItem | null>(null);

  // Per-path useWatch — narrow subscriptions to the two fields we touch.
  const currentRecipeId = useWatch({ control, name: `batchSets.${index}.recipeId` }) as
    | string
    | undefined;
  const currentSelections = useWatch({ control, name: `batchSets.${index}.selections` }) as
    | Record<string, unknown>
    | undefined;

  const recipesQuery = useQuery<RecipeListResponse>({
    queryKey: ["recipes", "accessible"],
    queryFn: () => api<RecipeListResponse>("/api/recipes?limit=100"),
  });

  const items: RecipeListItem[] = recipesQuery.data?.items ?? [];

  // Resolve the currently-selected recipe item from the list.
  const selectedItem =
    typeof currentRecipeId === "string" && currentRecipeId.length > 0
      ? items.find((it) => it.id === currentRecipeId)
      : undefined;

  function commitRecipeSelection(recipe: RecipeListItem): void {
    setValue(`batchSets.${index}.recipeId`, recipe.id, { shouldDirty: true });
    setValue(`batchSets.${index}.selections`, {}, { shouldDirty: true });
    setOpen(false);
    setPendingReplaceRecipe(null);
  }

  function handleSelect(recipe: RecipeListItem): void {
    // Replace-confirm: only when the BATCH SET already had user options
    // applied AND the user is picking a DIFFERENT recipe.
    const hasSelections =
      currentSelections !== undefined && Object.keys(currentSelections).length > 0;
    const isReplacement =
      typeof currentRecipeId === "string" &&
      currentRecipeId.length > 0 &&
      currentRecipeId !== recipe.id;
    if (hasSelections && isReplacement) {
      setPendingReplaceRecipe(recipe);
      return;
    }
    commitRecipeSelection(recipe);
  }

  const triggerLabel =
    selectedItem !== undefined
      ? selectedItem.name ?? selectedItem.slug ?? selectedItem.id
      : "Pick a recipe…";

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={`Pick a recipe for this ${subdivision.toLowerCase()}`}
            className="w-full justify-between"
            data-recipe-picker-trigger
          >
            <span
              className={
                selectedItem !== undefined ? "text-foreground" : "text-muted-foreground"
              }
            >
              {triggerLabel}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search recipes…" aria-label="Search recipes" />
            <CommandList>
              <CommandEmpty>No recipes match your search.</CommandEmpty>
              {recipesQuery.isLoading ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  Loading recipes…
                </div>
              ) : null}
              {items.length > 0 ? (
                <CommandGroup heading="ALL ACCESSIBLE RECIPES">
                  {items.map((recipe) => {
                    const label = recipe.name ?? recipe.slug ?? recipe.id;
                    const isCurrent = recipe.id === currentRecipeId;
                    return (
                      <CommandItem
                        key={recipe.id}
                        value={`${label} ${recipe.slug ?? ""}`}
                        onSelect={() => handleSelect(recipe)}
                      >
                        <Check
                          className={`mr-2 size-4 ${isCurrent ? "opacity-100" : "opacity-0"}`}
                          aria-hidden
                        />
                        <div className="flex flex-col">
                          <span>{label}</span>
                          {recipe.owner_id !== undefined ? (
                            <span className="text-xs text-muted-foreground">
                              {recipe.owner_id}
                            </span>
                          ) : null}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Replace-confirm Dialog — UI-SPEC line 392. */}
      <Dialog
        open={pendingReplaceRecipe !== null}
        onOpenChange={(o) => {
          if (!o) setPendingReplaceRecipe(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace recipe?</DialogTitle>
            <DialogDescription>
              Selecting{" "}
              <strong>
                {pendingReplaceRecipe?.name ??
                  pendingReplaceRecipe?.slug ??
                  pendingReplaceRecipe?.id ??
                  ""}
              </strong>{" "}
              will clear your current option selections in this {subdivision.toLowerCase()}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              autoFocus
              onClick={() => setPendingReplaceRecipe(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (pendingReplaceRecipe !== null) {
                  commitRecipeSelection(pendingReplaceRecipe);
                }
              }}
            >
              Replace recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
BatchSetRecipePicker.displayName = "BatchSetRecipePicker";
